import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { Language } from '../../../firestore-models/language'
import { ApiLanguage } from '../../../api-client-module';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-languages-dialog',
  templateUrl: './select-languages-dialog.component.html',
  styleUrls: ['./select-languages-dialog.component.css'],
  standalone: false,
})
export class SelectLanguagesDialogComponent implements OnInit, OnDestroy {

  private languagesCollection!: AngularFirestoreCollection<Language>;
  languagesObservable!: Observable<Language[]>;
  languagesSubscribe!: Subscription;

  displayedColumns: string[] = ['position', 'select', 'code', 'name'];
  dataSource = new MatTableDataSource<Language>();
  selection = new SelectionModel<Language>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  currentLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<SelectLanguagesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiLanguage,
    private angularFirestore: AngularFirestore,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.languagesCollection = this.angularFirestore.collection<Language>('languages');
    this.languagesObservable = this.languagesCollection.valueChanges({ idField: 'id' });
    this.languagesSubscribe = this.languagesObservable.subscribe(languages => {
      this.dataSource.data = languages;
    });
    this.currentLang = this.translate.currentLang;
  }

  ngOnDestroy(): void {
    this.languagesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Language): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectLanguages() {
    let languages: Array<ApiLanguage> = new Array<ApiLanguage>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const language: ApiLanguage = {
          id: row.id,
          lang_code: row.lang_code,
          lang_name: row.lang_name,
          lang_name_en: row.lang_name_en,
          display_name: row.display_name,
          trans_lang_code: row.trans_lang_code,
          tts_lang_code: row.tts_lang_code,
          tts_voice_codes: row.tts_voice_codes,
          enabled: row.enabled
        };
        languages.push(language);
      }
    }
    this.dialogRef.close(languages);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'code':
          return compare(a.lang_code, b.lang_code, isAsc);
        case 'name':
          return compare(a.lang_name, b.lang_name, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
